import { Directive, HostListener, ElementRef, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Directive({
  selector: '[percentFormatter]',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PercentFormatterDirective),
    multi: true
  }]
})
export class PercentFormatterDirective implements ControlValueAccessor {

  private onChange: (value: any) => void;
  private onTouched: () => void;

  private _value: number = null;

  constructor(private el: ElementRef<HTMLInputElement>) { }

  // от модела към екрана
  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this._value = null;
      this.el.nativeElement.value = '';
      return;
    }

    let num = Number(value);
    if (isNaN(num)) {
      this._value = null;
      this.el.nativeElement.value = '';
      return;
    }

    // гарантираме цяло число и граници 0–100
    num = Math.round(num);
    num = Math.max(0, Math.min(100, num));

    this._value = num;
    this.el.nativeElement.value = this.formatPercent(this._value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  // при фокус → показваме чистото число (без %)
  @HostListener('focus')
  onFocus() {
    if (this._value !== null && this._value !== undefined && !isNaN(this._value)) {
      this.el.nativeElement.value = this._value.toString();
    } else {
      this.el.nativeElement.value = '';
    }
  }

  // при писане → позволяваме само цифри, режем до 0–100, цяло число
  @HostListener('input', ['$event.target.value'])
  onInput(value: string) {
    // махаме всичко, което не е цифра
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly === '') {
      this._value = null;
      if (this.onChange) {
        this.onChange(null);
      }
      return;
    }

    let num = parseInt(digitsOnly, 10);

    if (isNaN(num)) {
      this._value = null;
      if (this.onChange) {
        this.onChange(null);
      }
      return;
    }

    // граници 0–100
    if (num < 0) { num = 0; }
    if (num > 100) { num = 100; }

    this._value = num;

    if (this.onChange) {
      this.onChange(this._value);
    }

    // в движение може да оставиш суровото число или да го форматираш – аз го оставям сурово докато пише
    this.el.nativeElement.value = this._value.toString();
  }

  // при blur → форматираме 12 → "12 %"
  @HostListener('blur')
  onBlur() {
    if (this.onTouched) {
      this.onTouched();
    }

    if (this._value === null || this._value === undefined || isNaN(this._value)) {
      this.el.nativeElement.value = '';
      return;
    }

    this.el.nativeElement.value = this.formatPercent(this._value);
  }

  private formatPercent(value: number): string {
    // цяло число, без дробна част
    return value.toLocaleString('bg-BG', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }) + ' %';
  }
}
